import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { secureGet, securePost } from '../../config/api';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { SaveCancelTop } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from '../../components/Breadcrumbs';
import LoadingLogo from '../../components/LoadingLogo';

import plusIcon from '../../icons/plus.svg';
import deleteIcon from '../../icons/trashR.svg';
import { formatAmount } from '../../utils/format';

const CreditNoteForm = ({
  title,
  baseFields = [],
  onSubmit,
  onSuccess,
  onCancel,
}) => {
  const location = useLocation();
  const prefilledDeliveryNote = location.state?.deliveryNote ?? null;
  console.log("prefilledDeliveryNote", prefilledDeliveryNote);

  const [formValues, setFormValues] = useState({});
  const [listEntities, setListEntities] = useState([]);
  const [prefilledRows, setPrefilledRows] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [deliveryNoteDetails, setDeliveryNoteDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [submitting, setSubmitting] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const isPrefilledMode = Boolean(prefilledDeliveryNote?.id);

  const deliveryNoteId = useMemo(() => {
    if (isPrefilledMode) return prefilledDeliveryNote.id;
    return formValues.deliveryNoteId ? Number(formValues.deliveryNoteId) : null;
  }, [isPrefilledMode, prefilledDeliveryNote?.id, formValues.deliveryNoteId]);

  console.log("prefilledDeliveryNote", prefilledDeliveryNote);
  useEffect(() => {
    if (isPrefilledMode) {
      const details = prefilledDeliveryNote?.details;
      const rows = (Array.isArray(details) ? details : []).map((d) => {
        const original = d.quantity ?? 0;
        const returned = d.returned ?? 0;
        const available = Math.max(0, original - returned);
        return {
          productId: d.product?.id ?? d.productId,
          productName: d.product?.name ?? d.designation ?? '',
          reference: d.product?.reference ?? d.reference ?? '',
          originalQuantity: original,
          returned,
          available,
          priceHT: d.unitPrice ?? 0,
          quantityToReturn: 0,
          tva: d.product?.tva ?? 0,
          discount: d.discount ?? 0,
        };
      });
      setPrefilledRows(rows);
    }
  }, [isPrefilledMode, prefilledDeliveryNote]);
  console.log("prefilledRows", prefilledRows);

  useEffect(() => {
    if (isPrefilledMode) return;
    const initialValues = {};
    baseFields.forEach((field) => {
      initialValues[field.name] = field.value ?? '';
    });
    setFormValues(initialValues);
  }, [baseFields, isPrefilledMode]);

  const handleInputChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const setQuantityToReturn = (index, value) => {
    const num = value === '' ? 0 : Math.max(0, parseInt(value, 10) || 0);
    setPrefilledRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const qty = Math.min(num, row.available);
        return { ...row, quantityToReturn: qty };
      })
    );
  };

  const handleAddClick = async () => {
    if (!deliveryNoteId) {
      showSnackbar(t('placeholder.empty_fields') || 'Veuillez sélectionner un bon de livraison', 'error');
      return;
    }
    setPopupOpen(true);
    setLoadingDetails(true);
    setDeliveryNoteDetails(null);
    try {
      const response = await secureGet(`/deliveryNotes/${deliveryNoteId}`, showSnackbar);
      const data = response?.data;
      const details = data?.carts?.[0]?.details ?? data?.details ?? [];
      setDeliveryNoteDetails(Array.isArray(details) ? details : []);
    } catch (e) {
      if (e?.name !== 'AbortError') setDeliveryNoteDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelectLine = (detail) => {
    const productId = detail.product?.id ?? detail.productId;
    const quantity = detail.quantity ?? 0;
    const productName = detail.product?.name ?? detail.designation ?? '';
    if (!productId || quantity <= 0) return;
    setListEntities((prev) => [
      ...prev,
      { productId, quantity, productName },
    ]);
  };

  const removeLine = (index) => {
    setListEntities((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!deliveryNoteId) {
      showSnackbar(t('placeholder.empty_fields') || 'Veuillez sélectionner un bon de livraison', 'error');
      return;
    }

    if (isPrefilledMode) {
      const details = prefilledRows
        .filter((r) => r.quantityToReturn > 0)
        .map((r) => ({ productId: r.productId, quantity: r.quantityToReturn }));
      const invalid = prefilledRows.some((r) => r.quantityToReturn > r.available);
      if (invalid) {
        showSnackbar('La quantité à retourner ne peut pas dépasser la quantité disponible.', 'error');
        return;
      }
      if (prefilledRows.length === 1 && (prefilledRows[0].quantityToReturn ?? 0) === 0) {
        showSnackbar('Aucune quantité à retourner : rien ne sera modifié.', 'error');
        return;
      }
      if (details.length === 0) {
        showSnackbar(t('placeholder.empty_fields') || 'Veuillez saisir au moins une quantité à retourner.', 'error');
        return;
      }
      try {
        setSubmitting(true);
        await securePost('/creditNotes/from-delivery-note', { deliveryNoteId, details }, showSnackbar);
        onSuccess?.();
        onSubmit?.();
      } catch (err) {
        if (err?.name !== 'AbortError') console.error(err);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (listEntities.length === 0) {
      showSnackbar(t('placeholder.empty_fields') || 'Veuillez ajouter au moins une ligne', 'error');
      return;
    }

    const payload = {
      deliveryNoteId,
      details: listEntities.map(({ productId, quantity }) => ({ productId, quantity })),
    };

    try {
      setSubmitting(true);
      await securePost('/creditNotes/from-delivery-note', payload, showSnackbar);
      onSuccess?.();
      onSubmit?.();
    } catch (err) {
      if (err?.name !== 'AbortError') console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <Helmet>
        <title>Drivnest - {t('titles.br')}</title>
      </Helmet>

      <div className="flex flex-col space-y-2">
        <Breadcrumbs />
        <div className="flex justify-between items-center">
          <h2 className="text-[32px] font-bold text-black">{title}</h2>
          <SaveCancelTop onCancel={onCancel} onSave={handleSubmit} loading={submitting} />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {!isPrefilledMode && (
          <div className="mt-4">
            <div className="grid grid-cols-3 gap-4 lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-2 xs:grid-cols-1">
              {baseFields.map((field, index) => (
                <Input
                  key={field.name || index}
                  type={field.type || 'text'}
                  label={field.label}
                  name={field.name}
                  value={formValues[field.name] ?? ''}
                  required={field.required}
                  onChange={(value) => handleInputChange(field.name, value)}
                  options={field.options || []}
                />
              ))}
            </div>
          </div>
        )}

        {isPrefilledMode && (
          <div className="mt-">
            <p className="text-[#38485C] font-semibold">
              Bon de livraison : {prefilledDeliveryNote?.deliveryNoteNumber ?? prefilledDeliveryNote?.id ?? '—'}
            </p>
          </div>
        )}

        {isPrefilledMode ? (
          <div className="mt-4 space-y-4">
            <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-">{t('fields_types.products')}</h3>
            {prefilledRows.map((row, index) => (
              <div
                key={index}
                className="grid grid-cols-[20%_10%_10%_10%_10%_10%_15%_15%] items-center bg-white p-2 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
              >
                <div>
                  <p className="font-bold text-[#043F88] text-[12px]">{row.reference}</p>
                  <p className="text-primary font-semibold">{row.productName || `#${row.productId}`}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">PRIX UNITAIRE HT</p>
                  <p className="text-[#043F88] text-[20px] font-medium">{formatAmount(row.priceHT)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">TVA (%)</p>
                  <p className="text-[#043F88] text-[20px] font-medium">{row.tva}%</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">DISCOUNT (%)</p>
                  <p className="text-[#043F88] text-[20px] font-medium">{row.discount}%</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">{t('rows.quantity').toUpperCase()}</p>
                  <p className="text-[#043F88] text-[20px] font-medium">{row.originalQuantity}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">RETOURNÉ</p>
                  <p className="text-[#043F88] text-[20px] font-medium">{row.returned}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">À retourner</p>
                  <input
                    type="number"
                    min={0}
                    max={row.available}
                    value={row.quantityToReturn}
                    onChange={(e) => setQuantityToReturn(index, e.target.value)}
                    className="w-full max-w-[80px] mx-auto text-center border border-gray-200 rounded-lg px-2 py-1 text-[#043F88] text-[20px] font-semibold focus:border-[#043F88] focus:outline-none"
                  />
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">TOTAL HT</p>
                    <p className="text-[#043F88] text-[20px] font-medium">{formatAmount((row.priceHT * row.quantityToReturn) * (1 - row.discount / 100) * (1 + row.tva / 100))}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div>
              <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">{t('fields_types.products')}</h3>
              <div className="flex items-end gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleAddClick}
                  disabled={!deliveryNoteId}
                  className="bg-[#16283E] text-white w-[48px] h-[48px] rounded-[12px] hover:bg-[#3F3456] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
                  title={t('primaryButtons.add') || 'Ajouter'}
                >
                  <img src={plusIcon} alt="Add" className="w-4 h-4" />
                </button>
                <span className="text-[#707070] text-sm">Sélectionner des lignes du bon de livraison</span>
              </div>
            </div>

        {listEntities.length > 0 && (
          <div className="mt-4">
            <div className="grid grid-cols-[2fr_1fr_0.5fr] items-center text-[#919191] text-sm font-semibold mb-2 px-6">
              <div className="text-left">{t('rows.product')}</div>
              <div className="text-center">{t('rows.quantity')}</div>
              <div className="text-right"></div>
            </div>
            {listEntities.map((line, index) => (
              <div
                key={index}
                className="grid grid-cols-[2fr_1fr_0.5fr] items-center py-4 px-6 rounded-[12px] bg-white shadow-md mb-2"
              >
                <div className="text-left">
                  <p className="text-[#6B78F8] text-[16px] font-semibold">{line.productName || `#${line.productId}`}</p>
                </div>
                <div className="text-center font-semibold text-[#38485C]">{line.quantity}</div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="w-8 h-8 flex items-center justify-center bg-[#F2F3F3] rounded-[12px] shadow-md cursor-pointer hover:bg-red-50"
                    onClick={() => removeLine(index)}
                  >
                    <img src={deleteIcon} alt="Delete" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}
      </form>

      {/* Popup: lines from delivery note */}
      {popupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPopupOpen(false)}>
          <div
            className="bg-white rounded-[16px] shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-[18px] font-semibold text-[#043F88]">
                {t('fields_types.list')} – Bon de livraison
              </h3>
              <button
                type="button"
                onClick={() => setPopupOpen(false)}
                className="text-[#707070] hover:text-[#043F88] font-medium"
              >
                Fermer
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {loadingDetails ? (
                <div className="flex justify-center py-12">
                  <LoadingLogo />
                </div>
              ) : !deliveryNoteDetails || deliveryNoteDetails.length === 0 ? (
                <p className="text-[#707070] text-center py-8">Aucune ligne sur ce bon de livraison.</p>
              ) : (
                <div className="space-y-2">
                  {deliveryNoteDetails.map((detail, index) => {
                    const productId = detail.product?.id ?? detail.productId;
                    const qty = detail.quantity ?? 0;
                    const name = detail.product?.name ?? detail.designation ?? `Ligne ${index + 1}`;
                    const unitPrice = detail.unitPrice ?? detail.product?.priceHT ?? 0;
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between gap-4 py-3 px-4 bg-gray-50 rounded-[12px]"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-[#38485C] font-semibold truncate">{name}</p>
                          <p className="text-[#707070] text-sm">
                            {t('rows.quantity')}: {qty} · {t('rows.priceuHT')}: {formatAmount(unitPrice)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleSelectLine(detail)}
                          className="flex-shrink-0 px-4 py-2 bg-[#043F88] text-white text-sm font-semibold rounded-[12px] hover:bg-[#062a5c]"
                        >
                          Sélectionner
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default CreditNoteForm;
